import {
    AbstractRecommendedNotificationParams,
    isVespaRecommendedNotificationParams,
} from '@pushly/models/lib/structs/journeys'
import { InvalidFieldException } from '@pushly/models/lib/exceptions/invalid-field-exception'
import { MissingFieldException } from '@pushly/models/lib/exceptions/missing-field-exception'
import { RecommendationSource } from '@pushly/models/lib/enums/recommendations/recommendation-source'
import { extractFriendlyErrorMessage } from '../../../../exceptions/helpers'
import { FEAT_INTEGRATIONS_BLUECONIC_REC, FEAT_INTEGRATIONS_VESPA_REC } from '../../../../constants'
import { BLUECONIC_DISPLAY_VALUE } from './blueconic/constants'
import { PUSHLY_AI_CURATOR_DISPLAY_VALUE } from './vespa/constants'

export type RecommendationSourceOption = { label: string; value: RecommendationSource }

/**
 * UI-level "content source" value for the unified feed/recommendation step editor.
 *
 * Feed is intentionally NOT a member of the models `RecommendationSource` enum —
 * a feed step is its own action type (`SEND_FEED_NOTIFICATION`) with its own
 * params. The editor unifies the *selector* only, so it models the feed option
 * as a UI-level string that lives alongside the two real recommendation sources.
 */
export const FEED_CONTENT_SOURCE = 'feed'

export type ContentSource = RecommendationSource.BLUECONIC | RecommendationSource.VESPA | typeof FEED_CONTENT_SOURCE

/** Display label for the Feed content-source option. */
export const FEED_SOURCE_DISPLAY_VALUE = 'Feed'

export type ContentSourceOption = { label: string; value: ContentSource }

/**
 * What a domain needs in order to use each recommendation source.
 *
 * The two sources are enabled by unrelated mechanisms and neither implies the
 * other: BlueConic is an active domain-integration record, Pushly AI Curator is
 * a domain flag with no integration entity of its own. Any check of the form
 * "can this domain run a recommendation step" must consult both, which is what
 * this type exists to make hard to forget.
 */
export type RecommendationSourceAvailability = {
    /** The active domain's enabled feature flags (`domain.flags`). */
    domainFlags: string[] | undefined
    /** Whether the domain has a BlueConic application/store a new step could point at. */
    hasBlueConicIntegration: boolean
}

/**
 * Source options for the unified content-source select: the recommendation
 * sources this domain can configure ({@link getRecommendationSourceOptions}),
 * plus a Feed option, which ships to all domains and so is always offered.
 *
 * Order matches the add-step precedence: BlueConic, Vespa, Feed.
 *
 * NOTE: this only controls which options are *offered* for a fresh selection.
 * The editor separately guarantees the currently-selected source is always
 * present, so an existing step still shows its own source on a domain that
 * could no longer choose it.
 */
export function getContentSourceOptions(availability: RecommendationSourceAvailability): ContentSourceOption[] {
    // getRecommendationSourceOptions only ever returns BlueConic/Vespa values,
    // both of which are members of ContentSource — widen the option type.
    const options: ContentSourceOption[] = getRecommendationSourceOptions(availability).map((o) => ({
        label: o.label,
        value: o.value as ContentSource,
    }))

    options.push({ label: FEED_SOURCE_DISPLAY_VALUE, value: FEED_CONTENT_SOURCE })

    return options
}

/**
 * Source options for the recommendation-source select — only the sources the
 * domain can actually carry through to a saved step.
 *
 * BlueConic needs an integration to name an application and store; Pushly AI
 * Curator needs only FEAT_INTEGRATIONS_VESPA_REC, since it has no integration
 * record to point at. The two sets are disjoint in practice: no domain enabled
 * for the Curator licenses BlueConic, so offering BlueConic unconditionally put
 * a vendor those customers have no relationship with in front of them, leading
 * to a picker with nothing in it.
 *
 * Returns plain data (no JSX) so the gating logic stays pure and unit testable.
 * The Select renders these labels as plain text; the Pushly AI brand mark lives
 * on the canvas node subtitle instead (see step-config.tsx), so the dropdown
 * stays a straight list of names.
 */
export function getRecommendationSourceOptions(
    availability: RecommendationSourceAvailability,
): RecommendationSourceOption[] {
    const options: RecommendationSourceOption[] = []

    // BlueConic needs both halves: the entitlement flag, and an integration to name
    // an application and store from. Dropping the flag withdraws the source without
    // touching the integration record, which is how it is turned off for a domain.
    if (availability.domainFlags?.includes(FEAT_INTEGRATIONS_BLUECONIC_REC) && availability.hasBlueConicIntegration) {
        options.push({ label: BLUECONIC_DISPLAY_VALUE, value: RecommendationSource.BLUECONIC })
    }

    if (availability.domainFlags?.includes(FEAT_INTEGRATIONS_VESPA_REC)) {
        options.push({ label: PUSHLY_AI_CURATOR_DISPLAY_VALUE, value: RecommendationSource.VESPA })
    }

    return options
}

/**
 * Whether the domain can configure any recommendation source at all. Gates the
 * Recommendation add-step entry, which would otherwise offer a step that cannot
 * be completed or saved.
 */
export function hasAnyRecommendationSource(availability: RecommendationSourceAvailability): boolean {
    return getRecommendationSourceOptions(availability).length > 0
}

/**
 * The source a newly added Recommendation step opens on — the first the domain
 * can configure, so a Curator-only domain does not land on a BlueConic form it
 * has no integration to fill in.
 *
 * Undefined when no source is usable; callers own that fallback, since a step
 * can still be opened on a domain that could no longer add it.
 */
export function getDefaultRecommendationSource(
    availability: RecommendationSourceAvailability,
): RecommendationSource | undefined {
    return getRecommendationSourceOptions(availability)[0]?.value
}

export const validateRecommendedNotification = (params: AbstractRecommendedNotificationParams): [boolean, string] => {
    try {
        params.validate()
    } catch (error) {
        if (error instanceof InvalidFieldException || error instanceof MissingFieldException) {
            const displayError = extractFriendlyErrorMessage(error, {
                fieldNamesMap: {
                    recommendation_source: 'Recommendation Source',
                    recommendation_config: 'Recommendation Configuration',
                    'recommendation_config.*.boosts': 'Recommendation Configuration boost',
                    'recommendation_config.*.boosts.*.value': 'Boost value',
                    'recommendation_config.*.boosts.*.rampUp': 'Boost ramp up',
                    'recommendation_config.*.boosts.*.algorithm': 'Boost algorithm',
                    'recommendation_config.*.filters': 'Filters',
                },
                startWithProperty: true,
            })

            return [false, displayError.message]
        }

        return [false, error instanceof Error ? error.message : 'Validation failed']
    }

    // Content Window (freshness_max_age) is a required field in the Vespa UI.
    // The model marks it optional (legacy configs predate the control), so the
    // required-field contract is enforced here rather than letting an empty
    // Content Window input save a null value.
    if (isVespaRecommendedNotificationParams(params)) {
        const missingWindow = (params.recommendation_config ?? []).some((config) => config.freshness_max_age == null)
        if (missingWindow) {
            return [false, 'Content Window is required for every configuration.']
        }
    }

    return [true, '']
}
